import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  getManifest,
  getPreviewManifest,
} from "../api/publicClientViewApi";
import { mapSdkError } from "../api/sdkClient";
import { registerClientViewServiceWorker } from "../pwa/registerClientViewServiceWorker";
import { buildBrandingCssVariables } from "../utils/branding";
import { applyExperimentOverrides } from "../utils/experiments";
import { enrichAutoMapaManifest } from "../utils/autoMapaManifestEnrichment";
import { enrichCasaMapaManifest } from "../utils/casaMapaManifestEnrichment";
import { enrichMidpointMediaManifest } from "../utils/midpointMediaManifestEnrichment";
import { getSessionId, getVisitorId } from "../utils/session";

const ClientViewContext = createContext(null);

const MANIFEST_CACHE_PREFIX = "midpoint_cv_manifest_cache:";

function readCachedManifest(slug) {
  try {
    const raw = localStorage.getItem(`${MANIFEST_CACHE_PREFIX}${slug}`);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeCachedManifest(slug, manifest, version) {
  try {
    localStorage.setItem(
      `${MANIFEST_CACHE_PREFIX}${slug}`,
      JSON.stringify({ manifest, version, cachedAt: Date.now() })
    );
  } catch {
    // ignore
  }
}

export function ClientViewProvider({ slug, previewToken = null, children }) {
  const [rawManifest, setRawManifest] = useState(null);
  const [status, setStatus] = useState("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [isPreview, setIsPreview] = useState(false);
  const [experimentAssignments, setExperimentAssignments] = useState({});
  const [cacheVersion, setCacheVersion] = useState(0);
  const [usedCachedManifest, setUsedCachedManifest] = useState(false);

  const visitorId = useMemo(() => getVisitorId(), []);
  const sessionId = useMemo(() => getSessionId(), []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setStatus("loading");
      setErrorMessage("");
      setRawManifest(null);
      setIsPreview(false);
      setUsedCachedManifest(false);

      try {
        const body = previewToken
          ? await getPreviewManifest(slug, previewToken)
          : await getManifest(slug);

        if (!cancelled) {
          setRawManifest(body.manifest);
          setIsPreview(Boolean(previewToken || body.preview));
          const version = Number(
            body.publishedVersionNumber || body.cacheVersion || body.manifest?.manifestVersion || 0
          );
          setCacheVersion(version);
          if (!previewToken && body.manifest) {
            writeCachedManifest(slug, body.manifest, version);
          }
          setStatus("ready");
          if (previewToken) {
            const meta = document.createElement("meta");
            meta.name = "robots";
            meta.content = "noindex, nofollow";
            meta.setAttribute("data-cv-preview", "true");
            document.head.appendChild(meta);
          }
        }
      } catch (error) {
        if (!cancelled) {
          const mapped = mapSdkError(error);
          const cached = !previewToken ? readCachedManifest(slug) : null;

          if (cached?.manifest) {
            setRawManifest(cached.manifest);
            setCacheVersion(Number(cached.version || 0));
            setUsedCachedManifest(true);
            setStatus("ready");
            setIsPreview(false);
            return;
          }

          setRawManifest(null);
          setStatus(mapped.status === 404 ? "unavailable" : "error");
          setErrorMessage(mapped.message || "Unable to load this site.");
        }
      }
    }

    if (slug) {
      load();
    }

    return () => {
      cancelled = true;
      document.querySelector('meta[data-cv-preview="true"]')?.remove();
    };
  }, [slug, previewToken]);

  useEffect(() => {
    if (!slug || isPreview || status !== "ready") {
      return;
    }
    registerClientViewServiceWorker(slug, cacheVersion || 1);
  }, [slug, cacheVersion, isPreview, status]);

  const { manifest, assignments } = useMemo(() => {
    if (!rawManifest) {
      return { manifest: null, assignments: {} };
    }
    try {
      const { manifest: overridden, assignments: nextAssignments } = applyExperimentOverrides(
        rawManifest,
        visitorId
      );
      return {
        manifest: enrichCasaMapaManifest(
          enrichAutoMapaManifest(enrichMidpointMediaManifest(overridden, slug), slug),
          slug
        ),
        assignments: nextAssignments,
      };
    } catch {
      return {
        manifest: enrichCasaMapaManifest(
          enrichAutoMapaManifest(enrichMidpointMediaManifest(rawManifest, slug), slug),
          slug
        ),
        assignments: {},
      };
    }
  }, [rawManifest, visitorId, slug]);

  useEffect(() => {
    setExperimentAssignments(assignments);
  }, [assignments]);

  const brandingStyle = useMemo(
    () => buildBrandingCssVariables(manifest),
    [manifest]
  );

  const primaryListingDataSource = useMemo(
    () =>
      (manifest?.dataSources || []).find(
        (source) =>
          (source.type === "canonical_real_estate" || source.type === "vehicle_market") &&
          source.enabled !== false
      ) || manifest?.dataSources?.[0],
    [manifest]
  );

  const value = useMemo(
    () => ({
      slug,
      manifest,
      rawManifest,
      dataSources: manifest?.dataSources || [],
      status,
      errorMessage,
      brandingStyle,
      primaryQueryProfileId: primaryListingDataSource?.queryProfileId || null,
      leadCaptureEnabled: manifest?.crm?.leadCapture?.enabled !== false,
      saveSearchEnabled: manifest?.features?.saveSearch !== false,
      savedSearchAlertsEnabled: manifest?.features?.savedSearchAlerts !== false,
      analyticsEnabled: manifest?.analytics?.enabled !== false,
      isPreview,
      visitorId,
      sessionId,
      experimentAssignments,
      cacheVersion,
      usedCachedManifest,
    }),
    [
      slug,
      manifest,
      rawManifest,
      status,
      errorMessage,
      brandingStyle,
      primaryListingDataSource,
      isPreview,
      visitorId,
      sessionId,
      experimentAssignments,
      cacheVersion,
      usedCachedManifest,
    ]
  );

  return (
    <ClientViewContext.Provider value={value}>{children}</ClientViewContext.Provider>
  );
}

export function useClientView() {
  const context = useContext(ClientViewContext);
  if (!context) {
    throw new Error("useClientView must be used within ClientViewProvider");
  }
  return context;
}

export function useOptionalClientView() {
  return useContext(ClientViewContext);
}
